import { h } from '@omni-torn/ui';

/**
 * The Discord bot's control panel (/panel). Sign-in: a server admin runs /omni panel in
 * Discord and opens the private link it gives (…/panel#code=…); the code is traded once for
 * a sign-in kept in this tab only (sessionStorage), sent as "Authorization: Bearer".
 * Every change saves straight away and is noted in the server's log channel by the bot.
 */

const STORE = 'omni-torn.panel';

interface Rule {
  off?: boolean;
  who?: 'everyone' | 'admins' | 'roles';
  roles?: string[];
  channels?: string[];
}
interface Field {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'select';
  default: number | boolean | string;
  min?: number;
  max?: number;
  choices?: Array<number | { value: string; label: string }>;
}
interface Detail {
  guild: { id: string; name: string };
  /** mode 'diagnostics': Omni-Torn's developer, read-only (the server granted no support access); 'support': granted. */
  you: { roles: boolean; mode?: 'admin' | 'support' | 'diagnostics' | null; readOnly?: boolean };
  /** On: its admins switched it on; running: it runs (the server is set up and approved). */
  modules: Array<{ id: string; name: string; description: string; on: boolean; running?: boolean; core: boolean; commands: string[] }>;
  /** Set up (`/omni setup`, the server terms agreed) and approved by Omni-Torn: only then do parts run. */
  server?: { ready: boolean; why: string | null; approval: string; setUp: boolean; kind: string | null; termsVersion: number | null; termsOutdated?: boolean; termsCurrent?: number };
  commands: Array<{ key: string; description: string; depth: number; module: string; protected: boolean; adminOnly?: boolean; rule: Rule | null }>;
  channels: Array<{ id: string; name: string; parent: string | null; botCanPost: boolean }>;
  roles: Array<{ id: string; name: string; managed: boolean; admin: boolean }>;
  general: { factionId: number | null; factionName: string | null; factionConfirmed: boolean; factionData: boolean; autoLink: boolean; nicknames: boolean; nickTemplate: string; membersIntent: boolean; logChannel: string | null; alertsChannel?: string | null; gwResultsChannel?: string | null; tornChannel: string | null; gwChannels?: string[]; botChannels: string[]; adminRoles: string[] };
  verification: { verified: string[]; unverified: string[]; factions: Record<string, string[]>; positions: Record<string, string[]>; exclude: string[] };
  /** Why faction alerts post or not (as /omni check). */
  health?: Array<{ ok: boolean; name: string; value: string }>;
  alerts: Array<{ type: string; label: string; help: string; fields: Field[]; config: { channel: string; role: string | null; opts: Record<string, unknown>; follows?: boolean } | null }>;
  banking: { channel: string; roles: string[] } | null;
  /** Banking per faction (a server of several factions). */
  bankingSets?: Array<{ factionId: number; name: string | null; config: { channel: string; roles: string[] } | null }>;
  /** Public bot F2: the setup steps, the faction ties, today's usage, support access and what's kept. */
  setup?: {
    kind: string | null;
    kinds: Array<{ id: string; name: string; what: string }>;
    legacy: boolean;
    yourKey: boolean | null;
    faction: boolean;
    logChannel: boolean;
    alertsChannel: boolean;
    adminRoles: number;
    parts: number;
    terms: { version: number; at: number } | null;
    termsVersion: number;
    termsRows: Array<{ id: string; name: string; text: string }>;
  };
  /** C1: up to `maxFactions`; `home`: this server is the faction's home server (giveaways from Torn post here). */
  factions?: Array<{ id: number; name: string | null; confirmed: boolean; tiedBy: { tornId: number; name: string | null } | null; at: number | null; home?: boolean; homePicked?: boolean }>;
  maxFactions?: number;
  /** C1: each faction's alerts here (the Alerts tab picks one). */
  alertSets?: Array<{ factionId: number; name: string | null; tied: boolean; config: Record<string, { channel: string; role: string | null; opts: Record<string, unknown>; follows?: boolean } | null> }>;
  usage?: { today: { factionKeys: number; lent: number; own: number; alerts: number }; factionWatch: number | null; lastPolled: number | null; lookupsPerHour: number; queued: number; refusals: number; problems: string[] };
  support?: { until: number; by: string; at: number } | null;
  data?: { giveaways: number; bankRequests: number; stakeouts: number };
}

class PanelError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

function session(): { token: string; until: number } | null {
  try {
    const s = JSON.parse(sessionStorage.getItem(STORE) ?? 'null') as { token?: unknown; until?: unknown } | null;
    if (s && typeof s.token === 'string' && typeof s.until === 'number' && s.until > Date.now()) return { token: s.token, until: s.until };
  } catch {
    /* no storage */
  }
  return null;
}
function forget(): void {
  try {
    sessionStorage.removeItem(STORE);
  } catch {
    /* no storage */
  }
}

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const s = session();
  let res: Response;
  try {
    res = await fetch(`/api/panel${path}`, {
      method,
      headers: { accept: 'application/json', ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...(s ? { authorization: `Bearer ${s.token}` } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new PanelError(0, 'Couldn’t reach the Omni-Torn server.');
  }
  const data = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
  if (!res.ok) {
    if (res.status === 401) forget();
    throw new PanelError(res.status, data?.error?.message ?? `The server answered ${res.status}.`);
  }
  return data as T;
}

/** "Saved." / the problem, next to the control that was changed. */
function saver(): { el: HTMLElement; run: (f: () => Promise<{ synced?: true | string } | unknown>) => Promise<boolean> } {
  const el = h('span', { class: 'save-status', role: 'status' });
  return {
    el,
    async run(f) {
      el.className = 'save-status';
      el.textContent = 'Saving…';
      try {
        const r = (await f()) as { synced?: true | string; why?: string | null } | null;
        if (r && typeof r.synced === 'string') {
          el.className = 'save-status warn';
          el.textContent = `Saved, but ${r.synced}`;
        } else if (r && typeof r.why === 'string') {
          el.className = 'save-status warn';
          el.textContent = `Saved. ${r.why}`;
        } else {
          el.className = 'save-status ok';
          el.textContent = 'Saved.';
        }
        return true;
      } catch (err) {
        el.className = 'save-status bad';
        el.textContent = (err as Error).message;
        return false;
      }
    },
  };
}

// ---------------------------------------------------------------- pickers

function checkList(items: Array<{ id: string; label: string; note?: string; disabled?: boolean }>, chosen: readonly string[], empty = 'None here.'): { el: HTMLElement; value: () => string[] } {
  const boxes = items.map((i) => {
    const box = h('input', { type: 'checkbox', value: i.id, checked: chosen.includes(i.id), disabled: !!i.disabled && !chosen.includes(i.id) }) as HTMLInputElement;
    return { box, row: h('label', { class: 'check' }, box, ` ${i.label}`, i.note ? h('span', { class: 'muted' }, ` ${i.note}`) : null) };
  });
  return {
    el: h('div', { class: 'checks' }, ...(boxes.length ? boxes.map((b) => b.row) : [h('span', { class: 'muted' }, empty)])),
    value: () => boxes.filter((b) => b.box.checked).map((b) => b.box.value),
  };
}

const channelItems = (d: Detail) => d.channels.map((c) => ({ id: c.id, label: `#${c.name}`, note: [c.parent ? `in ${c.parent}` : '', c.botCanPost ? '' : '— the bot can’t post here'].filter(Boolean).join(' ') }));
const roleItems = (d: Detail, giveable = false) => d.roles.map((r) => ({ id: r.id, label: `@${r.name}`, note: r.admin ? (giveable ? '(admin powers — never handed out)' : '(admin)') : r.managed ? '(managed by an app)' : '', disabled: giveable && (r.admin || r.managed) }));

function select(options: Array<{ value: string; label: string }>, current: string): HTMLSelectElement {
  const s = h('select', {}, ...options.map((o) => h('option', { value: o.value, selected: o.value === current }, o.label))) as HTMLSelectElement;
  return s;
}

function section(title: string, ...kids: Array<Node | string | null>): HTMLElement {
  return h('section', { class: 'panel' }, h('header', { class: 'panel-head' }, h('h2', {}, title)), h('div', { class: 'panel-body' }, ...kids));
}

// ---------------------------------------------------------------- tabs

function partsTab(d: Detail, reload: () => void): HTMLElement {
  return section(
    'Parts of the bot',
    h('p', { class: 'muted' }, 'Everything starts off in a new server. Switch on what you use: its commands appear in Discord within a moment, and disappear again when you switch it off (its settings are kept).'),
    ...d.modules.map((m) => {
      const s = saver();
      const box = h('input', { type: 'checkbox', checked: m.on, disabled: m.core }) as HTMLInputElement;
      box.addEventListener('change', async () => {
        if (await s.run(() => call('POST', `/guilds/${d.guild.id}/modules`, { id: m.id, on: box.checked }))) reload();
        else box.checked = !box.checked;
      });
      return h(
        'div',
        { class: 'card' },
        h('label', { class: 'switch' }, box, h('strong', {}, ` ${m.name}`), m.core ? h('span', { class: 'muted' }, ' — always on') : m.on && m.running === false ? h('span', { class: 'muted', 'data-field': 'waiting' }, ' — starts once the server is set up and approved') : null),
        h('div', { class: 'muted' }, m.description),
        h('div', { class: 'cmds' }, m.commands.map((c) => `/${c}`).join('  ')),
        s.el,
      );
    }),
  );
}

function commandsTab(d: Detail, reload: () => void): HTMLElement {
  const on = new Set(d.modules.filter((m) => m.on).map((m) => m.id));
  // /omni's subcommands are all "server admins only, always": one line says it.
  const rows = d.commands.filter((c) => !(c.protected && c.depth > 0)).map((c) => {
    const r = c.rule ?? {};
    const summary = c.protected
      ? 'Server admins only, always'
      : r.off
        ? 'Off'
        : [c.adminOnly ? 'Bot admins only (always)' : r.who === 'admins' ? 'Bot admins only' : r.who === 'roles' ? `Roles: ${(r.roles ?? []).map((id) => d.roles.find((x) => x.id === id)?.name ?? id).join(', ')}` : 'Everyone', r.channels?.length ? `in ${r.channels.map((id) => `#${d.channels.find((x) => x.id === id)?.name ?? id}`).join(', ')}` : ''].filter(Boolean).join(' · ');
    const head = h('div', { class: `cmd-row depth-${c.depth}${on.has(c.module) ? '' : ' part-off'}` }, h('code', {}, `/${c.key}`), h('span', { class: 'muted' }, ` ${c.description}`), h('span', { class: `rule${r.off ? ' off' : ''}` }, summary));
    if (c.protected) return head;
    const edit = h('button', { class: 'btn-small', type: 'button' }, 'Change');
    head.append(edit);
    const box = h('div', { class: 'cmd-edit', hidden: true });
    edit.addEventListener('click', () => {
      if (!box.hidden) return void (box.hidden = true);
      const off = h('input', { type: 'checkbox', checked: !!r.off }) as HTMLInputElement;
      const who = select(
        [
          { value: 'everyone', label: 'Everyone' },
          { value: 'admins', label: 'Bot admins only' },
          { value: 'roles', label: 'Members with these roles:' },
        ],
        r.who ?? 'everyone',
      );
      const roles = checkList(roleItems(d), r.roles ?? []);
      const chans = checkList(channelItems(d), r.channels ?? []);
      const s = saver();
      const save = h('button', { class: 'btn-small', type: 'button' }, 'Save');
      const clear = h('button', { class: 'btn-small', type: 'button' }, 'Back to normal');
      const send = (rule: Rule | null) => s.run(() => call('POST', `/guilds/${d.guild.id}/rules`, { key: c.key, rule })).then((ok) => ok && reload());
      save.addEventListener('click', () => void send(c.adminOnly ? { off: off.checked, channels: chans.value() } : { off: off.checked, who: who.value as Rule['who'], roles: roles.value(), channels: chans.value() }));
      clear.addEventListener('click', () => void send(null));
      box.replaceChildren(
        h('label', { class: 'check' }, off, ' Switch it off (it leaves this server’s command list)'),
        ...(c.adminOnly
          ? [h('p', { class: 'muted' }, 'A settings or admin command: always for bot admins only.')]
          : [h('div', { class: 'field' }, h('span', {}, 'Who can use it: '), who), h('details', {}, h('summary', {}, 'Roles (for “Members with these roles”)'), roles.el)]),
        h('details', {}, h('summary', {}, 'Only in these channels (none ticked: anywhere)'), chans.el),
        h('p', { class: 'muted' }, 'Bot admins can always use it, anywhere.'),
        h('div', {}, save, ' ', clear, ' ', s.el),
      );
      box.hidden = false;
    });
    return h('div', {}, head, box);
  });
  return section('Commands: who can use what', h('p', { class: 'muted' }, 'Greyed: its part of the bot is off. Limits on a command also cover its subcommands.'), ...rows);
}

/** The faction whose alerts the Alerts tab shows, per server (a server of several factions — C1). */
const alertFaction = new Map<string, number>();
/** The faction whose banking setup is shown, per server. */
const bankFaction = new Map<string, number>();

function alertsTab(d: Detail, reload: () => void): HTMLElement {
  const g = d.general;
  const sets = d.alertSets ?? [];
  const picked = sets.find((x) => x.factionId === alertFaction.get(d.guild.id)) ?? sets[0] ?? null;
  const pick =
    sets.length > 1
      ? (() => {
          const sel = select(sets.map((x) => ({ value: String(x.factionId), label: `${x.name ?? 'Faction'} [${x.factionId}]${x.tied ? '' : ' — not tied yet'}` })), String(picked?.factionId ?? ''));
          sel.setAttribute('data-field', 'alert-faction');
          sel.addEventListener('change', () => {
            alertFaction.set(d.guild.id, Number(sel.value));
            reload();
          });
          return h('label', { class: 'field' }, h('span', {}, 'Alerts of faction: '), sel);
        })()
      : null;
  const configOf = (type: string, fallback: Detail['alerts'][number]['config']) => (picked ? (picked.config[type] ?? null) : fallback);
  const confirmed = picked ? picked.tied : g.factionConfirmed;
  const note = !confirmed
    ? 'Faction alerts need the faction tied to this server by one of its leaders, co-leaders or members a leader named (Factions tab, or /omni faction).'
    : sets.length <= 1 && !g.factionData
      ? 'The bot has no key that reads your faction’s data yet: a member with the faction’s API access gives it one (Omni-Torn extension → options → Discord bot).'
      : null;
  const on = d.modules.find((m) => m.id === 'fac')?.on;
  const shared = g.alertsChannel ?? null;
  // Discord's **bold** and `code` shown as plain text.
  const plain = (t: string) => t.replace(/\*\*|`/g, '').replace(/<#(\d+)>/g, (_m, id) => `#${d.channels.find((c) => c.id === id)?.name ?? id}`);
  const health = d.health ?? [];
  const problems = health.filter((x) => !x.ok);
  return section(
    'Faction alerts',
    health.length
      ? h(
          'div',
          { class: problems.length ? 'notice' : 'muted', 'data-field': 'alerts-health' },
          h('strong', {}, problems.length ? 'Why alerts may not post:' : 'Alerts are working:'),
          h('ul', {}, ...(problems.length ? problems : health).map((x) => h('li', {}, `${x.ok ? '✅' : '❌'} ${x.name}: ${plain(x.value)}`))),
        )
      : null,
    h('p', { class: 'muted' }, shared ? 'Alerts set to “The alerts channel” post there and follow it if you change it (Faction & settings → Bot settings).' : 'Tip: set an alerts channel (Faction & settings → Bot settings) to send every alert there without picking a channel for each.'),
    !on ? h('p', { class: 'notice' }, 'The Faction part is off (Parts tab): alerts set here wait until it’s on.') : null,
    pick,
    note ? h('p', { class: 'notice' }, note) : null,
    ...d.alerts.map((a) => {
      const cfg = configOf(a.type, a.config);
      const fid = picked ? { factionId: picked.factionId } : {};
      // '' = the alerts channel (Bot settings): the alert follows it.
      const first = shared ? `The alerts channel (#${d.channels.find((c) => c.id === shared)?.name ?? shared})` : cfg?.follows ? 'The alerts channel (none set: it waits)' : '— pick a channel —';
      const chan = select([{ value: '', label: first }, ...d.channels.map((c) => ({ value: c.id, label: `#${c.name}${c.botCanPost ? '' : ' (bot can’t post)'}` }))], !cfg || cfg.follows ? '' : cfg.channel);
      const ping = select([{ value: '', label: 'Nobody' }, ...d.roles.map((r) => ({ value: r.id, label: `@${r.name}` }))], cfg?.role ?? '');
      const inputs = a.fields.map((f) => {
        const cur = cfg?.opts?.[f.key] ?? f.default;
        let input: HTMLInputElement | HTMLSelectElement;
        if (f.type === 'boolean') input = h('input', { type: 'checkbox', checked: cur === true }) as HTMLInputElement;
        else if (f.type === 'select') input = select((f.choices as Array<{ value: string; label: string }>) ?? [], String(cur));
        else if (f.choices) input = select((f.choices as number[]).map((n) => ({ value: String(n), label: String(n) })), String(cur));
        else input = h('input', { type: 'number', min: f.min, max: f.max, step: 1, value: String(cur) }) as HTMLInputElement;
        return { f, input, el: h('label', { class: 'field' }, h('span', {}, `${f.label}: `), input) };
      });
      const s = saver();
      const save = h('button', { class: 'btn-small', type: 'button' }, cfg ? 'Save' : 'Switch on');
      save.addEventListener('click', () => {
        const opts: Record<string, unknown> = {};
        for (const { f, input } of inputs) opts[f.key] = f.type === 'boolean' ? (input as HTMLInputElement).checked : f.type === 'select' ? input.value : Number(input.value);
        void s.run(() => call('POST', `/guilds/${d.guild.id}/alerts`, { type: a.type, channel: chan.value, role: ping.value || null, opts, ...fid })).then((ok) => ok && reload());
      });
      const off = h('button', { class: 'btn-small', type: 'button', hidden: !cfg }, 'Switch off');
      off.addEventListener('click', () => void s.run(() => call('POST', `/guilds/${d.guild.id}/alerts`, { type: a.type, off: true, ...fid })).then((ok) => ok && reload()));
      return h(
        'div',
        { class: `card${cfg ? ' is-on' : ''}` },
        h('div', {}, h('strong', {}, a.label), h('span', { class: `badge${cfg ? ' on' : ''}` }, cfg ? 'On' : 'Off')),
        h('div', { class: 'muted' }, a.help),
        h('label', { class: 'field' }, h('span', {}, 'Post in: '), chan),
        h('label', { class: 'field' }, h('span', {}, 'Ping: '), ping),
        ...inputs.map((i) => i.el),
        h('div', {}, save, ' ', off, ' ', s.el),
      );
    }),
  );
}

function rolesTab(d: Detail, reload: () => void): HTMLElement {
  const g = d.general;
  const s1 = saver();
  const auto = h('input', { type: 'checkbox', checked: g.autoLink }) as HTMLInputElement;
  const nick = h('input', { type: 'checkbox', checked: g.nicknames }) as HTMLInputElement;
  const tpl = h('input', { type: 'text', maxlength: 32, value: g.nickTemplate }) as HTMLInputElement;
  const save1 = h('button', { class: 'btn-small', type: 'button' }, 'Save');
  save1.addEventListener('click', () => void s1.run(() => call('POST', `/guilds/${d.guild.id}/general`, { autoLink: auto.checked, nicknames: nick.checked, nickTemplate: tpl.value })).then((ok) => ok && reload()));
  const linking = section(
    'Linking and nicknames',
    h('label', { class: 'check' }, auto, ' Link every member to their Torn account automatically'),
    !g.membersIntent ? h('p', { class: 'muted' }, 'The bot can’t see the member list yet (Server Members intent): members are linked when they use it.') : null,
    h('label', { class: 'check' }, nick, ' Rename linked members'),
    h('label', { class: 'field' }, h('span', {}, 'Name pattern ({name}, {id}, {tag}): '), tpl),
    h('div', {}, save1, ' ', s1.el),
  );
  const v = d.verification;
  const s2 = saver();
  const lists = {
    verified: checkList(roleItems(d, true), v.verified),
    unverified: checkList(roleItems(d, true), v.unverified),
    exclude: checkList(roleItems(d), v.exclude),
  };
  const mapRows = (m: Record<string, string[]>, keyLabel: string, keyType: 'number' | 'text') => {
    const box = h('div', {});
    const rows: Array<{ key: HTMLInputElement; roles: ReturnType<typeof checkList> }> = [];
    const add = (k: string, chosen: string[]) => {
      const key = h('input', { type: keyType, value: k, placeholder: keyLabel, maxlength: 40 }) as HTMLInputElement;
      const roles = checkList(roleItems(d, true), chosen);
      rows.push({ key, roles });
      box.append(h('div', { class: 'card' }, h('label', { class: 'field' }, h('span', {}, `${keyLabel}: `), key), h('details', { open: chosen.length > 0 }, h('summary', {}, 'Roles'), roles.el)));
    };
    for (const [k, l] of Object.entries(m)) add(k, l);
    const more = h('button', { class: 'btn-small', type: 'button' }, `Add a ${keyLabel.toLowerCase()}`);
    more.addEventListener('click', () => add('', []));
    return { el: h('div', {}, box, more), value: () => Object.fromEntries(rows.filter((r) => r.key.value.trim() && r.roles.value().length).map((r) => [r.key.value.trim(), r.roles.value()])) };
  };
  const factions = mapRows(v.factions, 'Faction id', 'number');
  const positions = mapRows(v.positions, 'Position', 'text');
  const save2 = h('button', { class: 'btn-small', type: 'button', disabled: !d.you.roles }, 'Save roles');
  save2.addEventListener('click', () =>
    void s2.run(() => call('POST', `/guilds/${d.guild.id}/verification`, { verified: lists.verified.value(), unverified: lists.unverified.value(), exclude: lists.exclude.value(), factions: factions.value(), positions: positions.value() })).then((ok) => ok && reload()),
  );
  const roles = section(
    'Verification roles',
    !d.you.roles ? h('p', { class: 'notice' }, 'Changing these needs Discord’s Manage Roles permission.') : null,
    h('p', { class: 'muted' }, 'The bot gives and takes only the roles set here (when members join, daily, and with /verify). Its own role must be above them.'),
    h('details', { open: true }, h('summary', {}, 'Linked members get'), lists.verified.el),
    h('details', {}, h('summary', {}, 'Members not linked get'), lists.unverified.el),
    h('details', {}, h('summary', {}, 'By faction'), factions.el),
    h('details', {}, h('summary', {}, 'By position in this server’s faction'), positions.el),
    h('details', {}, h('summary', {}, 'Never change members with'), lists.exclude.el),
    h('div', {}, save2, ' ', s2.el),
  );
  return h('div', {}, linking, roles);
}

function generalTab(d: Detail, reload: () => void): HTMLElement {
  const g = d.general;
  const s1 = saver();
  const fid = h('input', { type: 'number', min: 1, value: '', placeholder: 'Faction id', 'data-field': 'faction-id' }) as HTMLInputElement;
  const setF = h('button', { class: 'btn-small', type: 'button', 'data-action': 'tie' }, 'Add');
  // Only a faction id (never 0 here — that would remove every faction).
  setF.addEventListener('click', () => Number(fid.value) > 0 && void s1.run(() => call('POST', `/guilds/${d.guild.id}/faction`, { id: Number(fid.value) })).then((ok) => ok && setTimeout(reload, 1500)));
  const list = d.factions ?? [];
  const faction = section(
    `This server’s factions (up to ${d.maxFactions ?? 5})`,
    h('p', {}, list.length ? list.map((f) => `${f.name ?? 'Faction'} [${f.id}] — ${f.confirmed ? 'tied' : 'not tied yet'}`).join(' · ') : g.factionId ? `${g.factionName ?? 'Faction'} [${g.factionId}]` : 'None (a community server).'),
    h('div', { class: 'field' }, fid, ' ', setF, ' ', s1.el),
    h('p', { class: 'muted' }, 'Adding one here ties it when you (your linked Torn account) are its leader or co-leader, or a member a leader named on the website (Faction → Discord). Remove one, or pick the home server, in the Factions tab.'),
  );
  const s2 = saver();
  const log = select([{ value: '', label: 'Off' }, ...d.channels.map((c) => ({ value: c.id, label: `#${c.name}` }))], g.logChannel ?? '');
  const tornCh = select([{ value: '', label: 'Off' }, ...d.channels.map((c) => ({ value: c.id, label: `#${c.name}` }))], g.tornChannel ?? '');
  const alertsCh = select([{ value: '', label: 'None (each alert picks its own)' }, ...d.channels.map((c) => ({ value: c.id, label: `#${c.name}` }))], g.alertsChannel ?? '');
  const resultsCh = select([{ value: '', label: 'The log channel' }, ...d.channels.map((c) => ({ value: c.id, label: `#${c.name}` }))], g.gwResultsChannel ?? '');
  const gwCh = checkList(channelItems(d), g.gwChannels ?? []);
  const botCh = checkList(channelItems(d), g.botChannels);
  const admins = checkList(roleItems(d), g.adminRoles);
  const save = h('button', { class: 'btn-small', type: 'button' }, 'Save');
  save.addEventListener('click', () => void s2.run(() => call('POST', `/guilds/${d.guild.id}/general`, { logChannel: log.value || null, alertsChannel: alertsCh.value || null, gwResultsChannel: resultsCh.value || null, tornChannel: tornCh.value || null, gwChannels: gwCh.value(), botChannels: botCh.value(), adminRoles: admins.value() })).then((ok) => ok && reload()));
  const rest = section(
    'Bot settings',
    h('label', { class: 'field' }, h('span', {}, 'Log channel (every change made here, and anything without its own channel below): '), log),
    h('label', { class: 'field', 'data-field': 'alerts-channel' }, h('span', {}, 'Alerts channel (faction alerts set to it, and the bot’s notes about alerts): '), alertsCh),
    h('label', { class: 'field', 'data-field': 'gw-results-channel' }, h('span', {}, 'Giveaway results (winners, rerolls, cancellations, prize proofs): '), resultsCh),
    h('label', { class: 'field' }, h('span', {}, 'Giveaways started in Torn post in: '), tornCh),
    h(
      'details',
      { 'data-field': 'gw-channels' },
      h('summary', {}, `Giveaways started in Discord post only in (${(g.gwChannels ?? []).length ? (g.gwChannels ?? []).map((id) => `#${d.channels.find((c) => c.id === id)?.name ?? id}`).join(', ') : 'none ticked: where they’re started'})`),
      h('p', { class: 'muted' }, 'Started anywhere else, a giveaway goes to the first one ticked. Tick the same channel as above to keep every giveaway, from Torn or Discord, in one place.'),
      gwCh.el,
    ),
    h('details', {}, h('summary', {}, 'Members use the bot only in (none ticked: everywhere)'), botCh.el),
    h('details', {}, h('summary', {}, 'Bot-admin roles (besides Administrator / Manage Server)'), admins.el),
    h('div', {}, save, ' ', s2.el),
  );
  const s3 = saver();
  // Banking is set up per faction (a server of several: pick which).
  const bsets = d.bankingSets ?? [];
  const bpicked = bsets.find((x) => x.factionId === bankFaction.get(d.guild.id)) ?? bsets[0] ?? null;
  const bank = bpicked ? bpicked.config : d.banking;
  const bfid = bpicked ? { factionId: bpicked.factionId } : {};
  const bpick =
    bsets.length > 1
      ? (() => {
          const sel = select(bsets.map((x) => ({ value: String(x.factionId), label: `${x.name ?? 'Faction'} [${x.factionId}]` })), String(bpicked?.factionId ?? ''));
          sel.setAttribute('data-field', 'bank-faction');
          sel.addEventListener('change', () => {
            bankFaction.set(d.guild.id, Number(sel.value));
            reload();
          });
          return h('label', { class: 'field' }, h('span', {}, 'Banking of faction: '), sel);
        })()
      : null;
  const bch = select([{ value: '', label: '— pick a channel —' }, ...d.channels.map((c) => ({ value: c.id, label: `#${c.name}` }))], bank?.channel ?? '');
  const bankers = checkList(roleItems(d), bank?.roles ?? []);
  const bsave = h('button', { class: 'btn-small', type: 'button' }, 'Save');
  bsave.addEventListener('click', () => void s3.run(() => call('POST', `/guilds/${d.guild.id}/banking`, { channel: bch.value, roles: bankers.value(), ...bfid })).then((ok) => ok && reload()));
  const boff = h('button', { class: 'btn-small', type: 'button', hidden: !bank }, 'Switch off');
  boff.addEventListener('click', () => void s3.run(() => call('POST', `/guilds/${d.guild.id}/banking`, { off: true, ...bfid })).then((ok) => ok && reload()));
  const banking = section(
    'Banking requests',
    h('p', { class: 'muted' }, '/withdraw requests go to this channel, pinging the bankers (needs the Banking part on).'),
    bpick,
    h('label', { class: 'field' }, h('span', {}, 'Bankers’ channel: '), bch),
    h('details', { open: true }, h('summary', {}, 'Banker roles'), bankers.el),
    h('div', {}, bsave, ' ', boff, ' ', s3.el),
  );
  return h('div', {}, faction, rest, banking);
}

// ---------------------------------------------------------------- public bot F2: setup, factions, usage, data, support

/** Discord's markup (**bold**, `code`, <#channel>, <t:…:t>) as plain text. */
function plainText(d: Detail, t: string): string {
  return t
    .replace(/\*\*|`/g, '')
    .replace(/<#(\d+|[\w-]+)>/g, (_m, id) => `#${d.channels.find((c) => c.id === id)?.name ?? id}`)
    .replace(/<t:(\d+):[a-zA-Z]>/g, (_m, sec) => new Date(Number(sec) * 1000).toLocaleString());
}

const when = (ms: number) => new Date(ms).toLocaleString();
const step = (done: boolean | null, title: string, ...kids: Array<Node | string | null>) =>
  h('li', { class: `setup-step${done ? ' done' : ''}`, 'data-done': done ? 'yes' : 'no' }, h('span', { class: 'setup-mark', 'aria-hidden': 'true' }, done ? '✅' : done === null ? '➖' : '⬜'), h('div', {}, h('strong', {}, title), ...kids));

function setupTab(d: Detail, reload: () => void): HTMLElement {
  const st = d.setup;
  if (!st) return section('Setup', h('p', { class: 'muted' }, 'Run /omni setup in Discord.'));
  const ro = !!d.you.readOnly;
  const s1 = saver();
  const kind = select([{ value: '', label: '— pick one —' }, ...st.kinds.map((k) => ({ value: k.id, label: k.name }))], st.kind ?? '');
  kind.disabled = ro;
  kind.addEventListener('change', () => kind.value && void s1.run(() => call('POST', `/guilds/${d.guild.id}/setup`, { kind: kind.value })).then((ok) => ok && reload()));
  const agreed = st.terms && st.terms.version >= st.termsVersion;
  const s2 = saver();
  const agree = h('button', { type: 'button', class: 'btn-small', 'data-action': 'agree', disabled: ro || d.you.mode !== 'admin' || !st.yourKey }, 'I agree for this server') as HTMLButtonElement;
  agree.addEventListener('click', () => void s2.run(() => call('POST', `/guilds/${d.guild.id}/setup`, { agree: true })).then((ok) => ok && reload()));
  const community = st.kind === 'community';
  return section(
    'Setup',
    h('p', { class: 'muted' }, 'The same steps as ', h('code', {}, '/omni setup'), ' in Discord. The parts start once setup is finished and Omni-Torn has approved this server.'),
    st.legacy ? h('p', { class: 'notice', 'data-field': 'legacy' }, 'This server was set up before setup existed — it keeps working. Agree to the bot’s server terms once (step 7).') : null,
    h(
      'ol',
      { class: 'plain-list setup-steps', 'data-field': 'setup-steps' },
      step(!!st.kind, '1. What kind of server', h('div', { class: 'field' }, kind, ' ', s1.el), st.kind ? h('div', { class: 'muted small' }, st.kinds.find((k) => k.id === st.kind)?.what ?? '') : null),
      step(st.yourKey, '2. Your Torn key', h('div', { class: 'muted small' }, st.yourKey === null ? 'Each admin gives their own (Omni-Torn’s developer can’t see it).' : st.yourKey ? 'Omni-Torn keeps a key of yours — the same key you sign in with on the extension, the website and Torn PDA.' : 'Give it in Discord with /account key (Torn’s terms table first), or sign in on the extension or the website with it — one key for all.')),
      step(community ? null : st.faction, '3. Your faction', h('div', { class: 'muted small' }, community ? 'Not for a community server.' : st.faction ? 'Tied (Factions tab).' : 'Tie it in the Factions & settings tab, or with /omni faction (a leader, co-leader or key giver of it).')),
      step(st.logChannel, '4. Channels', h('div', { class: 'muted small' }, `Log channel: ${st.logChannel ? 'set' : 'not set'} · alerts channel: ${st.alertsChannel ? 'set' : 'not set'} (Faction & settings → Bot settings).`)),
      step(st.adminRoles > 0 ? true : null, '5. Bot-admin roles', h('div', { class: 'muted small' }, st.adminRoles ? `${st.adminRoles} role${st.adminRoles === 1 ? '' : 's'} besides Administrator / Manage Server.` : 'None besides Administrator / Manage Server (optional).')),
      step(st.parts > 0, '6. Parts', h('div', { class: 'muted small' }, `${st.parts} switched on (Parts tab).`)),
      step(
        !!agreed,
        `7. The bot’s server terms (version ${st.termsVersion})`,
        agreed ? h('div', { class: 'muted small', 'data-field': 'terms-agreed' }, `Agreed (version ${st.terms!.version}) on ${when(st.terms!.at)}.`) : null,
        agreed ? null : h('details', { open: true }, h('summary', {}, 'Read them'), h('dl', { class: 'bot-terms' }, ...st.termsRows.flatMap((r) => [h('dt', {}, r.name), h('dd', {}, r.text)]))),
        agreed ? null : h('div', {}, agree, ' ', s2.el, d.you.mode === 'admin' && !st.yourKey ? h('span', { class: 'muted small' }, ' Give your key first (step 2).') : null),
      ),
    ),
  );
}

function factionsTab(d: Detail, reload: () => void): HTMLElement {
  const list = d.factions ?? [];
  return section(
    'Factions',
    h('p', { class: 'muted' }, `A faction’s data (alerts, balances, member lists) is used here only once its leader or co-leader — or a member a leader named — ties it. Up to ${d.maxFactions ?? 5} factions; faction commands pick each member’s own. A faction’s home server gets the giveaways started in Torn (its leaders pick it here or on the website).`),
    list.length
      ? h(
          'ul',
          { class: 'plain-list', 'data-field': 'factions' },
          ...list.map((f) => {
            const s = saver();
            const rm = h('button', { type: 'button', class: 'btn-small', 'data-action': 'untie', hidden: !!d.you.readOnly }, 'Remove') as HTMLButtonElement;
            rm.addEventListener('click', () => void s.run(() => call('POST', `/guilds/${d.guild.id}/faction`, { id: f.id, remove: true })).then((ok) => ok && reload()));
            const home = h('button', { type: 'button', class: 'btn-small', 'data-action': 'home', hidden: !!d.you.readOnly || !f.confirmed || !!f.homePicked }, 'Make this its home server') as HTMLButtonElement;
            home.addEventListener('click', () => void s.run(() => call('POST', `/guilds/${d.guild.id}/faction`, { id: f.id, home: true })).then((ok) => ok && reload()));
            return h(
              'li',
              { class: 'card', 'data-faction': String(f.id) },
              h('div', {}, h('strong', {}, `${f.name ?? 'Faction'} [${f.id}]`), ' ', h('span', { class: `badge${f.confirmed ? ' on' : ''}` }, f.confirmed ? 'Tied' : 'Not tied'), f.home ? h('span', { class: 'badge on', 'data-field': 'home' }, '🏠 Home server') : null),
              h('div', { class: 'muted small', 'data-field': 'tied-by' }, f.confirmed && f.tiedBy ? `Tied by ${f.tiedBy.name ?? 'player'} [${f.tiedBy.tornId}]${f.at ? ` on ${when(f.at)}` : ''}.` : f.confirmed ? 'Tied.' : 'Its data stays off until its leader or co-leader, or a member a leader named, ties it (/omni faction).'),
              h('div', {}, rm, ' ', home, ' ', s.el),
            );
          }),
        )
      : h('p', { 'data-field': 'no-factions' }, 'No faction tied (fine for a community server). Set one in Faction & settings or with /omni faction.'),
  );
}

function usageTab(d: Detail): HTMLElement {
  const u = d.usage;
  if (!u) return section('Usage', h('p', { class: 'muted' }, 'Not available.'));
  const row = (label: string, value: string | number, field: string) => h('tr', { 'data-row': field }, h('th', {}, label), h('td', { class: 'num' }, String(value)));
  return section(
    'Usage today',
    h('p', { class: 'muted' }, 'Since midnight (UTC). Torn calls for this server’s look-ups (linking, /whois, nicknames, giveaway checks), by whose key paid.'),
    h(
      'div',
      { class: 'table-wrap' },
      h(
        'table',
        { class: 'usage-table', 'data-field': 'usage' },
        h(
          'tbody',
          {},
          row('Torn calls — your faction members’ keys', u.today.factionKeys, 'faction-keys'),
          row('Torn calls — lent keys (the shared pool)', u.today.lent, 'lent'),
          u.today.own ? row('Torn calls — Omni-Torn’s own key', u.today.own, 'own') : null,
          u.factionWatch !== null ? row('Your faction’s watch (alerts, boards — shared by every server tied to it)', u.factionWatch, 'watch') : null,
          row('Alerts posted', u.today.alerts, 'alerts'),
          row('Last faction round', u.lastPolled ? when(u.lastPolled) : '—', 'last-round'),
          row('Background look-ups allowed an hour', u.lookupsPerHour, 'per-hour'),
          row('Members waiting to be looked up', u.queued, 'queued'),
          row('Discord refusals (last 10 minutes)', u.refusals, 'refusals'),
        ),
      ),
    ),
    h('h3', {}, 'Problems'),
    u.problems.length ? h('ul', { class: 'notice', 'data-field': 'problems' }, ...u.problems.map((p) => h('li', {}, plainText(d, p)))) : h('p', { class: 'muted', 'data-field': 'problems' }, 'None — everything here is working.'),
  );
}

function dataTab(d: Detail, reload: () => void): HTMLElement {
  const own = d.you.mode === 'admin';
  const k = d.data ?? { giveaways: 0, bankRequests: 0, stakeouts: 0 };
  const s1 = saver();
  const exp = h('button', { type: 'button', class: 'btn-small', 'data-action': 'export', disabled: !own }, 'Export as a file') as HTMLButtonElement;
  exp.addEventListener('click', () =>
    void s1.run(async () => {
      const data = await call<unknown>('POST', `/guilds/${d.guild.id}/export`, {});
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      const a = h('a', { href: url, download: `omni-torn-server-${d.guild.id}.json`, hidden: true });
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return null;
    }),
  );
  const s2 = saver();
  const confirm = h('input', { type: 'text', placeholder: 'Type DELETE', maxlength: 10, 'aria-label': 'Type DELETE to confirm', disabled: !own, 'data-field': 'delete-confirm' }) as HTMLInputElement;
  const del = h('button', { type: 'button', class: 'btn-small danger', 'data-action': 'delete', disabled: !own }, 'Delete this server’s data now') as HTMLButtonElement;
  del.addEventListener('click', () => void s2.run(() => call('POST', `/guilds/${d.guild.id}/delete`, { confirm: confirm.value.trim() })).then((ok) => ok && reload()));
  return section(
    'This server’s data',
    !own ? h('p', { class: 'notice' }, 'Only this server’s own admins (Discord’s Administrator permission) can export or delete its data.') : null,
    h('p', {}, 'Omni-Torn keeps this server’s settings (setup, parts, channels, roles, command limits, alerts), its stakeouts, its usage numbers (no end date) and what its parts recorded: ', h('span', { 'data-field': 'kept' }, `${k.giveaways} giveaways, ${k.bankRequests} bank requests, ${k.stakeouts} stakeouts`), '.'),
    h('h3', {}, 'Export'),
    h('p', { class: 'muted' }, 'One JSON file with the settings, stakeouts and usage numbers (who agreed to the bot’s server terms is left out). The full giveaway log: /giveaway export in Discord.'),
    h('div', {}, exp, ' ', s1.el),
    h('h3', {}, 'Delete now'),
    h('p', { class: 'muted' }, 'The settings, setup, parts, command limits, alerts, stakeouts and usage numbers go at once. Giveaways and bank requests recorded here stay (Omni-Torn keeps observations; members delete theirs with /account delete). The bot stays with every part off — /omni setup starts again; a server not set up within 30 days is left. Removing the bot does the same after 30 days.'),
    h('div', { class: 'field' }, confirm, ' ', del, ' ', s2.el),
  );
}

function supportTab(d: Detail, reload: () => void): HTMLElement {
  const own = d.you.mode === 'admin';
  const g = d.support ?? null;
  const s = saver();
  const hours = h('input', { type: 'number', min: 1, max: 72, step: 1, value: '24', 'aria-label': 'Hours', disabled: !own, 'data-field': 'support-hours' }) as HTMLInputElement;
  const allow = h('button', { type: 'button', class: 'btn-small', 'data-action': 'support-allow', disabled: !own }, g ? 'Change the end' : 'Allow') as HTMLButtonElement;
  allow.addEventListener('click', () => void s.run(() => call('POST', `/guilds/${d.guild.id}/support`, { hours: Number(hours.value) })).then((ok) => ok && reload()));
  const end = h('button', { type: 'button', class: 'btn-small', 'data-action': 'support-end', hidden: !g || !own }, 'End it now') as HTMLButtonElement;
  end.addEventListener('click', () => void s.run(() => call('POST', `/guilds/${d.guild.id}/support`, { end: true })).then((ok) => ok && reload()));
  return section(
    'Support access',
    h('p', {}, 'Omni-Torn’s developer sees this server’s panel ', h('strong', {}, 'read-only'), ' (diagnostics) unless you allow more. With support access he counts as a bot admin here for a while and can change settings — never who’s a bot admin, never this server’s data (export or delete). Each command he runs and each change is posted in the log channel.'),
    h('p', { 'data-field': 'support-status', class: g ? 'notice' : 'muted' }, g ? `On until ${when(g.until)}.` : 'Off.'),
    own ? h('div', { class: 'field' }, h('span', {}, 'For '), hours, h('span', {}, ' hours (1–72) '), allow, ' ', end, ' ', s.el) : h('p', { class: 'muted' }, 'Only this server’s own admins (Administrator) can change it — also with /omni support allow · end · status in Discord.'),
  );
}

// ---------------------------------------------------------------- pages

const TABS: Array<[string, string, (d: Detail, reload: () => void) => HTMLElement]> = [
  ['setup', 'Setup', setupTab],
  ['parts', 'Parts', partsTab],
  ['commands', 'Commands', commandsTab],
  ['alerts', 'Alerts', alertsTab],
  ['roles', 'Linking & roles', rolesTab],
  ['general', 'Faction & settings', generalTab],
  ['factions', 'Factions', factionsTab],
  ['usage', 'Usage', (d) => usageTab(d)],
  ['data', 'Data', dataTab],
  ['support', 'Support access', supportTab],
];

function signInPage(view: HTMLElement, problem?: string): void {
  view.replaceChildren(
    section(
      'Bot control panel',
      problem ? h('p', { class: 'notice' }, problem) : null,
      h('p', {}, 'To sign in, run ', h('code', {}, '/omni panel'), ' in your Discord server and open the link it gives you (it works once, for 10 minutes). It’s for the server’s admins (Discord’s Administrator permission).'),
    ),
  );
}

/** A phone: Discord opens links in its own small browser there, so we try the real one first. */
const PHONE = /Android|iPhone|iPad|iPod/i;

/**
 * The same link for the phone's own browser. Android: an intent link (opens the default browser);
 * iPhone/iPad: Safari's own link (iOS 17+). The code goes in the address (?code=) because the
 * intent link needs the # for itself; it still works only once.
 */
export function outsideLink(ua: string, host: string, code: string): string | null {
  if (/Android/i.test(ua)) return `intent://${host}/panel?code=${code}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
  if (/iPhone|iPad|iPod/i.test(ua)) return `x-safari-https://${host}/panel?code=${code}`;
  return null;
}

/**
 * Owner's call 2026-10-17: no question, straight to the phone's own browser. A web page can't
 * force Discord's built-in browser to let go, so this tries once; if we're still here and in
 * front a moment later, Discord kept us and the panel just opens here instead. If the phone's
 * browser did open (this page went to the background), the code is left for it.
 */
function leaveDiscord(view: HTMLElement, code: string): void {
  const link = outsideLink(navigator.userAgent, location.host, code);
  if (!link) return void redeem(view, code);
  view.replaceChildren(h('div', { class: 'panel loading' }, 'Opening your browser…'));
  let left = false;
  // Hidden (another app came to the front) or lost focus (iOS asks "Open in Safari?").
  const onHide = (e: Event) => {
    if (e.type !== 'visibilitychange' || document.visibilityState === 'hidden') left = true;
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onHide);
  window.addEventListener('blur', onHide);
  const a = h('a', { href: link, hidden: true });
  document.body.append(a);
  a.click();
  a.remove();
  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onHide);
    window.removeEventListener('blur', onHide);
    if (!left && document.visibilityState === 'visible' && document.hasFocus()) return void redeem(view, code);
    const here = h('button', { type: 'button', class: 'big' }, 'Open it here instead');
    here.addEventListener('click', () => void redeem(view, code));
    view.replaceChildren(
      section(
        'Bot control panel',
        h('p', {}, 'The panel opened in your browser — you can close this.'),
        h('div', { class: 'open-out' }, here),
        h('p', { class: 'muted' }, 'The link works once: if your browser already used it, run /omni panel again.'),
      ),
    );
  }, 1500);
}

export async function renderPanel(view: HTMLElement): Promise<void> {
  document.title = 'Bot control panel · Omni-Torn';
  // The link from /omni panel (#code=…, or ?code=… when opened from the phone's own browser).
  const code = /^#code=([\w-]{16,64})$/.exec(location.hash)?.[1] ?? /^[\w-]{16,64}$/.exec(new URLSearchParams(location.search).get('code') ?? '')?.[0];
  if (code) {
    // Discord's link has #code=; the phone's own browser comes back with ?code= (already outside).
    const fromDiscord = location.hash.startsWith('#code=');
    history.replaceState(null, '', '/panel');
    if (PHONE.test(navigator.userAgent) && fromDiscord) return leaveDiscord(view, code);
    return redeem(view, code);
  }
  if (!session()) return signInPage(view);
  const server = new URLSearchParams(location.search).get('server');
  if (!server) return serverList(view);
  return serverPage(view, server, new URLSearchParams(location.search).get('tab') ?? 'parts');
}

/** Trade the one-time code for a sign-in in this tab, then open that server. */
async function redeem(view: HTMLElement, code: string): Promise<void> {
  try {
    const r = await call<{ token: string; until: number; guildId: string }>('POST', '/redeem', { code });
    sessionStorage.setItem(STORE, JSON.stringify({ token: r.token, until: r.until }));
    history.replaceState(null, '', `/panel?server=${encodeURIComponent(r.guildId)}`);
  } catch (err) {
    return signInPage(view, (err as Error).message);
  }
  return serverPage(view, new URLSearchParams(location.search).get('server') ?? '', 'parts');
}

async function serverList(view: HTMLElement): Promise<void> {
  view.replaceChildren(h('div', { class: 'panel loading' }, 'Loading your servers…'));
  try {
    const r = await call<{ guilds: Array<{ id: string; name: string }> }>('GET', '/guilds');
    view.replaceChildren(
      section(
        'Your servers',
        r.guilds.length ? h('ul', { class: 'servers' }, ...r.guilds.map((g) => h('li', {}, h('a', { href: `/panel?server=${encodeURIComponent(g.id)}` }, g.name)))) : h('p', {}, 'The bot isn’t in a server where you’re an admin (Administrator).'),
        signOut(view),
      ),
    );
  } catch (err) {
    if ((err as PanelError).status === 401) return signInPage(view, (err as Error).message);
    view.replaceChildren(section('Bot control panel', h('p', { class: 'notice' }, (err as Error).message)));
  }
}

function signOut(view: HTMLElement): HTMLElement {
  const b = h('button', { class: 'btn-small', type: 'button', title: 'Signs you out of the control panel everywhere' }, 'Sign out');
  b.addEventListener('click', async () => {
    // Everywhere: every sign-in made so far stops working (not just this tab's).
    await call('POST', '/signout', {}).catch(() => undefined);
    forget();
    history.replaceState(null, '', '/panel');
    signInPage(view, 'Signed out.');
  });
  return b;
}

async function serverPage(view: HTMLElement, guildId: string, tab: string): Promise<void> {
  const box = h('div', {});
  let current = TABS.some(([id]) => id === tab) ? tab : 'parts';
  const load = async (keepScroll = false) => {
    const y = window.scrollY;
    let d: Detail;
    try {
      d = await call<Detail>('GET', `/guilds/${encodeURIComponent(guildId)}`);
    } catch (err) {
      if ((err as PanelError).status === 401) return signInPage(view, (err as Error).message);
      view.replaceChildren(section('Bot control panel', h('p', { class: 'notice' }, (err as Error).message), h('p', {}, h('a', { href: '/panel' }, 'Your servers'))));
      return;
    }
    document.title = `${d.guild.name} · Bot control panel · Omni-Torn`;
    const tabs = h(
      'nav',
      { class: 'tabs', role: 'tablist' },
      ...TABS.map(([id, label]) => {
        const a = h('a', { href: `/panel?server=${encodeURIComponent(guildId)}&tab=${id}`, role: 'tab', class: id === current ? 'tab active' : 'tab', 'aria-selected': id === current ? 'true' : 'false' }, label);
        a.addEventListener('click', (e) => {
          e.preventDefault();
          current = id;
          history.replaceState(null, '', a.getAttribute('href'));
          void load();
        });
        return a;
      }),
    );
    const make = TABS.find(([id]) => id === current)![2];
    box.replaceChildren(
      h('div', { class: 'panel-top' }, h('h1', {}, d.guild.name), h('span', { class: 'grow' }), h('a', { href: '/panel' }, 'Your servers'), ' ', signOut(view)),
      ...(d.server && !d.server.ready ? [h('p', { class: 'notice', 'data-field': 'not-ready' }, d.server.why ?? 'This server’s parts don’t run yet.')] : []),
      ...(d.server?.termsOutdated
        ? [h('p', { class: 'notice', 'data-field': 'terms-changed' }, `The bot’s server terms changed (now version ${d.server.termsCurrent ?? ''}; this server agreed to version ${d.server.termsVersion ?? ''}). The bot keeps running. Read and agree in the Setup tab — your next change here also counts as agreeing.`)]
        : []),
      ...(d.you.readOnly ? [h('p', { class: 'notice', 'data-field': 'read-only' }, 'Read-only (diagnostics): this server hasn’t given you support access, so nothing here can be changed. Its admins can with /omni support allow.')] : []),
      ...(d.you.mode === 'support' ? [h('p', { class: 'muted', 'data-field': 'support' }, 'Support access: this server’s admins let you change its settings for a while. Each change is posted in its log channel.')] : []),
      tabs,
      make(d, () => void load(true)),
    );
    if (keepScroll) window.scrollTo(0, y);
  };
  view.replaceChildren(h('div', { class: 'panel loading' }, 'Loading…'));
  await load();
  if (box.childElementCount) view.replaceChildren(box);
}
